<template>
  <v-list>
    <v-list-item v-for="warranty in warranties" :key="warranty.label">
      <template v-slot:prepend>
        <v-icon :icon="warranty.icon" :color="warranty.color" />
      </template>
      <v-list-item-title>{{ warranty.label }}</v-list-item-title>
      <v-list-item-subtitle>{{ warranty.value }}</v-list-item-subtitle>
    </v-list-item>
  </v-list>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { parseSpecValue } from '@/utils/modelDetails'

import type { TrimData, ModelData } from '@/types'

const props = defineProps<{
  trim: TrimData | null
  modelData: ModelData | null
}>()

const getWarrantyValue = (key: string): string => {
  const trimWarranty = props.trim?.warranty as
    | Record<string, string>
    | undefined
  const modelWarranty = props.modelData?.warranty as
    | Record<string, string>
    | undefined
  const warranty = trimWarranty?.[key] || modelWarranty?.[key]
  if (warranty) {
    return typeof warranty === 'string' ? warranty : String(warranty)
  }

  const items = props.trim?.technicalSpecification?.Warranty?.items || []
  const item = items.find((i: string) =>
    i.toLowerCase().includes(key.toLowerCase()),
  )
  if (item) {
    const value = parseSpecValue(item)
    return value !== 'N/A' ? value : 'Contact dealer'
  }

  return 'Contact dealer for details'
}

const warranties = computed(() => [
  {
    label: 'Basic Warranty',
    value: getWarrantyValue('basic'),
    icon: 'mdi-shield-car',
    color: 'primary',
  },
  {
    label: 'Powertrain Warranty',
    value: getWarrantyValue('powertrain'),
    icon: 'mdi-engine',
    color: 'success',
  },
  {
    label: 'Corrosion Warranty',
    value: getWarrantyValue('corrosion'),
    icon: 'mdi-water-off',
    color: 'warning',
  },
  {
    label: 'Roadside Assistance',
    value: getWarrantyValue('roadside'),
    icon: 'mdi-tow-truck',
    color: 'info',
  },
  {
    label: 'Complimentary Maintenance',
    value: getWarrantyValue('maintenance'),
    icon: 'mdi-wrench',
    color: 'secondary',
  },
])
</script>
